// packages/client/src/common/utils/priceTiers.js
/**
 * Calendar price tiers (spec 63, quote editor v7 §7.4): a range-based
 * 3-tier split of the bookable per-person prices in the visible grid —
 * low ≤ min + span/3 < medium ≤ min + 2·span/3 < high. Shared by the lead
 * wizard's CalendarStep (which shows low/high only) and the quote editor's
 * room dialog (all three). Import-free so the client check script can load
 * it with plain node.
 *
 * All prices are integer cents, as /calendar-availability returns them
 * (the `rounded*` variants are whole euros expressed in cents).
 */

const BOOKABLE = ['available', 'alternatives'];

const dayKey = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return date.slice(0, 10);
  if (date.isValid === false) return null;
  return typeof date.toFormat === 'function' ? date.toFormat('yyyy-MM-dd') : null;
};

/** Per-person price (cents) of a bookable row in the display mode, or null. */
export function rowPrice(row, priceDisplayMode = 'gross') {
  if (!row || !BOOKABLE.includes(row.state)) return null;
  const raw = priceDisplayMode === 'net'
    ? (row.roundedPricePerPersonNet ?? row.pricePerPersonNet)
    : (row.roundedPricePerPerson ?? row.pricePerPerson);
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Bracket limits in cents; null when no bookable price is visible. */
export function computePriceBrackets(rows, priceDisplayMode = 'gross') {
  const prices = (rows || [])
    .map((row) => rowPrice(row, priceDisplayMode))
    .filter((value) => value != null);
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min;
  return { lowMax: min + span / 3, mediumMax: min + (2 * span) / 3 };
}

const findRow = (rows, date) => {
  const key = dayKey(date);
  if (!key) return null;
  return (rows || []).find((row) => dayKey(row.date) === key) ?? null;
};

/** Per-person price in cents of the day's bookable row, or null. */
export function dayPriceOf(rows, date, priceDisplayMode = 'gross') {
  return rowPrice(findRow(rows, date), priceDisplayMode);
}

export function dayTierOf(rows, brackets, date, priceDisplayMode = 'gross') {
  if (!brackets) return null;
  const price = dayPriceOf(rows, date, priceDisplayMode);
  if (price == null) return null;
  if (price > brackets.mediumMax) return 'high';
  if (price <= brackets.lowMax) return 'low';
  return 'medium';
}
